import { SxProp, View } from 'dripsy';

import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { DateFormat } from '@/utils';
import { formatDate } from '@/utils/dateFormatting';
import { Moment } from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '../dateRangePicker/Calendar';
import { useDatePickerSelector } from './useDatePickerSelector';

interface DatePickerSelectorProps {
  isOpen?: boolean;
  value: Moment | null;
  options?: {
    minDate?: Moment | null;
    maxDate?: Moment | null;
    yearInputFlag?: boolean;
  };
  displayMode: 'TOP' | 'BOTTOM';
  onValueChange?: (value: Moment | null) => void;
  onClose?: () => void;
  onConfirm?: () => void;
  onReset?: () => void;
  displayBothFlag: boolean;
}

export const DatePickerSelector: React.FC<DatePickerSelectorProps> = ({
  isOpen,
  value,
  options,
  displayMode,
  onValueChange,
  onClose,
  onConfirm,
  onReset,
  displayBothFlag
}) => {
  const { t } = useTranslation('earn');
  const selector = useDatePickerSelector({ isOpen, value, onValueChange });

  if (!isOpen) return null;

  return (
    <View
      sx={{
        ...styles.wrapper,
        ...{ top: displayMode === 'BOTTOM' ? '$11' : undefined, bottom: displayMode === 'TOP' ? '$11' : undefined }
      }}>
      <View sx={styles.container}>
        <Calendar
          side="LEFT"
          mode="DATE"
          selectedDate={selector.selectedDate}
          selectedDateRange={{
            from: null,
            to: null
          }}
          options={options}
          calendar={selector.calendarLeft}
          onSelectDate={selector.onSelectDate}
          onNextMonth={selector.onNextMonth}
          onPreviousMonth={selector.onPreviousMonth}
          onNextYear={selector.onNextYear}
          onYear={selector.onYear}
          onPreviousYear={selector.onPreviousYear}
        />
        {displayBothFlag && (
          <Calendar
            side="RIGHT"
            mode="DATE"
            selectedDate={selector.selectedDate}
            selectedDateRange={{
              from: null,
              to: null
            }}
            calendar={selector.calendarRight}
            onSelectDate={selector.onSelectDate}
            onNextMonth={selector.onNextMonth}
            onPreviousMonth={selector.onPreviousMonth}
            onNextYear={selector.onNextYear}
            onPreviousYear={selector.onPreviousYear}
          />
        )}
      </View>
      <Divider sx={styles.divider} />
      <View sx={styles.bottom}>
        <View sx={{ ...styles.viewDateSelectedWrapper, width: displayBothFlag ? undefined : 186 }}>
          <TextCaption secondary>{t('earn-invest::glossary.date-selected')}</TextCaption>
          <TextBody>
            {selector.selectedDate ? formatDate(selector.selectedDate.toString(), { format: DateFormat.DATE_FORMAT.NO_TIME }) : ' - '}
          </TextBody>
        </View>
        <View sx={styles.viewButtonWrapper}>
          <UniButton
            label={t('earn-invest::glossary.close')}
            size="small"
            variant={ButtonVariant.outline1}
            onPress={() => onClose && onClose()}
            wrapperSx={{ width: 70 }}
          />
          <UniButton
            label={t('earn-invest::glossary.reset')}
            size="small"
            variant={ButtonVariant.outline1}
            onPress={() => {
              selector.onReset();
              onReset && onReset();
            }}
            wrapperSx={{ width: 70 }}
          />
          <UniButton
            label={t('earn-invest::glossary.done')}
            size="small"
            onPress={() => onConfirm && onConfirm()}
            wrapperSx={{ width: 70 }}
          />
        </View>
      </View>
    </View>
  );
};

enum StyleName {
  wrapper = 'wrapper',
  container = 'container',
  top = 'top',
  bottom = 'bottom',
  divider = 'divider',
  viewDateSelectedWrapper = 'viewDateSelectedWrapper',
  viewButtonWrapper = 'viewButtonWrapper'
}

const styles: Record<StyleName, SxProp> = {
  wrapper: {
    position: 'absolute',
    top: '$10',
    left: 0,
    padding: '$3',
    borderRadius: 5,
    backgroundColor: '$backgroundPopup',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 100
  },
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '$5'
  },
  top: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '$5'
  },
  bottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    pt: '$4',
    pb: '$1'
  },
  divider: {
    mt: '$3'
  },
  viewDateSelectedWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$3'
  },
  viewButtonWrapper: {
    flexDirection: 'row',
    gap: '$3',
    alignItems: 'center',
    ml: '$4'
  }
};
